import {
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table'
import { ChevronDown } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import SyncFiltersWithURL from 'components/admin/SyncFiltersWithUrl'
import WorkflowsContext from 'components/context/WorkflowsContext'
import RowSelectionCountDisplay from 'components/reusable/table/RowSelectionCountDisplay'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select'
import { Button, Switch } from 'slab'
import { getGetBrokersTaskWorkflowsQueryKey } from 'utils/web_api'
import { type BrokersWorkflow } from 'utils/web_api.schemas'

import BulkUpdateWorkflowsDialog from './BulkUpdateWorkflowsDialog'
import EvaluateTaskStatusToggle from './EvaluateTaskStatusToggle'
import WorkflowsFiltersView, {
  filterConfigurations,
  type WorkflowFilterGroupType,
} from './WorkflowsFiltersView'
import {
  transformFiltersToParams,
  type WorkflowsFilters,
} from './workflowsHelpers'
import WorkflowsQuickFilters from './WorkflowsQuickFilters'
import WorkflowsTable from './WorkflowsTable'
import {
  WorkflowStatusDocumentationProvider,
  WorkflowStatusDocumentationSheet,
} from './WorkflowStatusDocumentationSheet'

// Column visibility mapping for workflows
const columnVisibilityMapping = {
  created_at: 'Created',
  'enrollment_info.member_name': 'Member',
  'enrollment_info.carrier_name': 'Carrier',
  'enrollment_info.employer_name': 'Employer',
  'enrollment_info.brokerage_name': 'Brokerage',
  'enrollment_info.coverage_type': 'Coverage',
  source: 'Source',
  coverage_start_date: 'Coverage Start',
  enrollment_event: 'Event Type',
  workflow_type: 'Type',
  status: 'Status',
  started_at: 'Started',
  completed_at: 'Completed',
  next_up_tasks: 'Next Task(s)',
  brokers: 'Assignees',
}

const defaultColumnVisibility = {
  created_at: true,
  'enrollment_info.member_name': true,
  'enrollment_info.carrier_name': true,
  'enrollment_info.employer_name': true,
  'enrollment_info.brokerage_name': true,
  'enrollment_info.coverage_type': true,
  source: true,
  coverage_start_date: true,
  enrollment_event: true,
  workflow_type: true,
  status: true,
  started_at: true,
  completed_at: true,
  next_up_tasks: true,
  brokers: true,
}

const MAX_NUMBER_OF_SELECTED_ITEMS = 100

const WorkflowsQueryView = () => {
  const [filters, setFilters] = useState<WorkflowsFilters>({})

  // Persist page size in localStorage
  const [pageSize, setPageSize] = useLocalStorage<number>(
    'workflows-records-per-page',
    20
  )

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize,
  })
  // Default sorting by status (pending first), then by created_at desc
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'status', desc: false },
  ])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  // Persist column visibility in localStorage
  const [columnVisibilitySettings, setColumnVisibilitySettings] =
    useLocalStorage<VisibilityState>(
      'workflows-column-visibility-settings',
      defaultColumnVisibility
    )

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    columnVisibilitySettings
  )

  // Persist "open in new tab" setting in localStorage
  const [openInNewTab, setOpenInNewTab] = useLocalStorage<boolean>(
    'workflows-open-in-new-tab',
    false
  )

  // Persist "evaluate task status" setting in localStorage
  const [evaluateTaskStatus, setEvaluateTaskStatus] = useLocalStorage<boolean>(
    'workflows-query-view-evaluate-task-status',
    false
  )

  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false)
  const [isBulkUpdateOpen, setIsBulkUpdateOpen] = useState(false)

  const numberOfSelectedItems =
    Object.values(rowSelection).filter(Boolean).length

  // Sync column visibility changes to localStorage
  useEffect(() => {
    setColumnVisibilitySettings(columnVisibility)
  }, [columnVisibility, setColumnVisibilitySettings])

  const handlePageSizeChange = (value: string) => {
    const newPageSize = parseInt(value, 10)
    setPageSize(newPageSize) // Persist to localStorage
    setPagination((prev) => ({
      ...prev,
      pageSize: newPageSize,
      pageIndex: 1, // Reset to first page when changing page size
    }))
  }

  // Generate the query key using the same params that the hook uses
  const params = transformFiltersToParams(filters, pagination, sorting, evaluateTaskStatus)
  const getWorkflowsQueryKey = getGetBrokersTaskWorkflowsQueryKey(params)

  return (
    <WorkflowStatusDocumentationProvider>
      <WorkflowsContext.Provider
        value={{
          getWorkflowsQueryKey,
        }}
      >
        <div className='max-w-[2400px] mx-auto px-4 pb-6'>
          <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2400px] mx-auto'>
            <BulkUpdateWorkflowsDialog
              isOpen={isBulkUpdateOpen}
              setIsOpen={setIsBulkUpdateOpen}
              selectedItems={Object.entries(rowSelection).reduce<string[]>(
                (acc, [key, value]) => (value ? [...acc, key] : acc),
                []
              )}
            />

            <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
              <div className='text-2xl font-semibold w-96'>Workflows</div>
              <div className='flex items-center justify-end w-full gap-x-2'>
                <Button
                  variant='tertiary-destructive'
                  onClick={() => setFilters({})}
                >
                  Clear Filters
                </Button>

                <DropdownMenu
                  open={isActionsMenuOpen}
                  onOpenChange={setIsActionsMenuOpen}
                >
                  <DropdownMenuTrigger asChild>
                    <Button
                      type='button'
                      variant='secondary'
                      data-testid='actions-menu'
                    >
                      <div className='flex items-center'>
                        Actions
                        <ChevronDown className='ml-1' size={12} />
                      </div>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>Columns</DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          {Object.entries(columnVisibilityMapping).map(
                            ([key, label]) => {
                              return (
                                <DropdownMenuCheckboxItem
                                  key={key}
                                  checked={
                                columnVisibility[
                                  key as keyof typeof columnVisibility
                                ]
                              }
                                  onCheckedChange={(checked) => {
                                    setColumnVisibility((prev) => ({
                                      ...prev,
                                      [key]: checked,
                                    }))
                                  }}
                                >
                                  {label}
                                </DropdownMenuCheckboxItem>
                              )
                            }
                          )}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>Settings</DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem
                            onSelect={(e) => {
                              e.preventDefault()
                            }}
                          >
                            <div className='flex items-center justify-between w-full gap-3'>
                              <span>Open in new tab</span>
                              <Switch
                                checked={openInNewTab}
                                onChange={(e) => setOpenInNewTab(e.target.checked)}
                              />
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  type='button'
                  variant='primary'
                  disabled={numberOfSelectedItems === 0}
                  onClick={() => setIsBulkUpdateOpen(true)}
                >
                  Update Selected
                </Button>
              </div>
            </div>

            <WorkflowsQuickFilters setFilters={setFilters} />
            <WorkflowsFiltersView
              filters={filters}
              setFilters={setFilters}
              pagination={pagination}
              setPagination={setPagination}
            />
            <SyncFiltersWithURL<
          WorkflowsFilters,
          BrokersWorkflow,
          WorkflowFilterGroupType
        >
              filters={filters}
              setFilters={setFilters}
              pagination={pagination}
              sortableKeys={['created_at', 'status', 'workflow_type', 'started_at']}
              filterConfigurations={filterConfigurations}
            />

            <div className='h-10 mb-2 flex items-center justify-between w-full'>
              <RowSelectionCountDisplay
                numberOfSelectedItems={numberOfSelectedItems}
                setRowSelection={setRowSelection}
                maxNumberOfSelectedItems={MAX_NUMBER_OF_SELECTED_ITEMS}
              />
              <div className='max-w-[50%] flex items-center justify-end space-x-4'>
                <EvaluateTaskStatusToggle
                  checked={evaluateTaskStatus}
                  onChange={setEvaluateTaskStatus}
                />
                <div className='flex items-center space-x-2'>
                  <div className='font-light text-sm text-nowrap'>
                    Records per page
                  </div>
                  <div className='w-[100px]'>
                    <Select
                      value={pagination.pageSize.toString()}
                      onValueChange={handlePageSizeChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder='Page size' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='10'>10</SelectItem>
                        <SelectItem value='20'>20</SelectItem>
                        <SelectItem value='50'>50</SelectItem>
                        <SelectItem value='100'>100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <WorkflowsTable
              filters={filters}
              setFilters={setFilters}
              pagination={pagination}
              setPagination={setPagination}
              sorting={sorting}
              setSorting={setSorting}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              columnVisibilityOverrides={columnVisibility}
              openInNewTab={openInNewTab}
              evaluateTaskStatus={evaluateTaskStatus}
            />
          </div>
        </div>
        <WorkflowStatusDocumentationSheet />
      </WorkflowsContext.Provider>
    </WorkflowStatusDocumentationProvider>
  )
}

export default WorkflowsQueryView
